import {
	type ProjectJSONValue,
	resolveProjectNodeInputValues,
} from "@oblique/shared/runtime"
import { mutators } from "@oblique/shared/zero"
import { useZero } from "@rocicorp/zero/react"
import type { Node, NodeProps } from "@xyflow/react"
import { Position, useStore, useUpdateNodeInternals } from "@xyflow/react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useParams } from "react-router"
import { cn } from "@/lib/utils"
import type { ProjectNodeData } from "@/types/project"
import { useProjectCanvasMode } from "../canvas-mode"
import { BuiltInControl, resolveBuiltInControlKind } from "./built-in-controls"
import { OutputHandle } from "./custom-handle"
import { ImageNode, imageNodeSource } from "./image-node"
import { getNodeValueRailConfig, getOutputHandleTone } from "./node-appearance"
import { NodeHeader } from "./node-header"
import { NodeValueRail } from "./node-value-rail"
import { resolveNodeParameters } from "./parameter-utils"
import { NodeSurface } from "./primitives/node-surface"
import { ReactNodeRenderer } from "./react-node-renderer"
import { SentenceRenderer } from "./sentence-renderer"
import { connectedViewerGeometry } from "./viewer-inputs"

export const BaseNode = (
	props: NodeProps<
		Node<ProjectNodeData> & {
			type: "python" | "react"
		}
	>,
) => {
	const updateNodeInternals = useUpdateNodeInternals()
	const z = useZero()
	const { projectId } = useParams<{ projectId: string }>()
	const edges = useStore((state) => state.edges)
	const nodes = useStore((state) => state.nodes)
	const data = props.data
	const { readOnly } = useProjectCanvasMode()
	const parameters = useMemo(
		() => resolveNodeParameters(data.inputs),
		[data.inputs],
	)
	const outputValues = useMemo(
		() =>
			new Map(
				nodes.flatMap((node) =>
					Object.hasOwn(node.data, "result")
						? [[node.id, node.data.result] as const]
						: [],
				),
			),
		[nodes],
	)
	const runtimeEdges = useMemo(
		() =>
			edges.map((edge) => ({
				sourceHandle: edge.sourceHandle ?? undefined,
				sourceNodeID: edge.source,
				targetHandle: edge.targetHandle ?? undefined,
				targetNodeID: edge.target,
			})),
		[edges],
	)
	const resolvedInputValues = useMemo(
		() =>
			resolveProjectNodeInputValues(
				data.id,
				data.inputs,
				runtimeEdges,
				outputValues,
			),
		[data.id, data.inputs, outputValues, runtimeEdges],
	)
	const connectionInfo = useMemo(() => {
		const inputs = new Set<string>()
		const resolvedInputs = new Set<string>()
		const sourceLabels = new Map<string, string>()
		for (const edge of edges) {
			if (edge.target === data.id && edge.targetHandle) {
				inputs.add(edge.targetHandle)
				if (outputValues.has(edge.source)) resolvedInputs.add(edge.targetHandle)
				const sourceNode = nodes.find((node) => node.id === edge.source) as
					| Node<ProjectNodeData>
					| undefined
				const fallbackLabel =
					typeof edge.sourceHandle === "string" && edge.sourceHandle.trim()
						? edge.sourceHandle
						: sourceNode?.data?.label
				if (fallbackLabel) sourceLabels.set(edge.targetHandle, fallbackLabel)
			}
		}
		return { inputs, resolvedInputs, sourceLabels, values: resolvedInputValues }
	}, [nodes, edges, data.id, outputValues, resolvedInputValues])
	const handleParameterChange = useCallback(
		(paramName: string, value: unknown) => {
			if (readOnly || !projectId) return
			if (value === undefined) {
				z.mutate(
					mutators.nodes.clearInputValue({
						project_id: projectId,
						node_id: data.id,
						input_id: paramName,
					}),
				)
				return
			}
			z.mutate(
				mutators.nodes.setInputValue({
					project_id: projectId,
					node_id: data.id,
					input_id: paramName,
					value: value as ProjectJSONValue,
				}),
			)
		},
		[projectId, data.id, readOnly, z],
	)
	const handleDeleteNode = useCallback(
		(event: React.MouseEvent) => {
			event.stopPropagation()
			if (readOnly || !projectId) return
			z.mutate(
				mutators.nodes.delete({
					project_id: projectId,
					node_id: data.id,
				}),
			)
		},
		[data.id, projectId, readOnly, z],
	)
	const isSelected = props.selected === true
	const outputTone = getOutputHandleTone(data.status)
	const railConfig = useMemo(
		() =>
			data.type === "react"
				? null
				: getNodeValueRailConfig(data.label, parameters),
		[data.label, data.type, parameters],
	)
	const inputHandleKey = useMemo(
		() => Array.from(connectionInfo.inputs).sort().join("|"),
		[connectionInfo.inputs],
	)
	const builtInControlKind = useMemo(
		() => resolveBuiltInControlKind(data.metadata, data.inputs, data.outputs),
		[data.inputs, data.metadata, data.outputs],
	)
	const imageSource = imageNodeSource(data.metadata, data.inputs)
	const [imageDimensions, setImageDimensions] = useState<
		{ width: number; height: number } | undefined
	>()
	const handleImageDimensionsChange = useCallback(
		(dimensions: { width: number; height: number }) => {
			setImageDimensions(dimensions)
		},
		[],
	)
	useEffect(() => {
		if (!imageDimensions) return
		const frame = requestAnimationFrame(() => updateNodeInternals(data.id))
		return () => cancelAnimationFrame(frame)
	}, [data.id, imageDimensions, updateNodeInternals])
	const reactNodeProps = useMemo(() => {
		if (
			data.metadata.built_in_kind !== "viewer" ||
			!Object.hasOwn(data.inputs, "geometry")
		) {
			return resolvedInputValues
		}
		const geometry = connectedViewerGeometry(data.id, edges, outputValues)
		return { ...resolvedInputValues, geometry }
	}, [
		data.id,
		data.inputs,
		data.metadata.built_in_kind,
		edges,
		outputValues,
		resolvedInputValues,
	])
	const parameterLayoutKey = useMemo(
		() =>
			parameters
				.map((parameter) => {
					const sourceLabel =
						connectionInfo.sourceLabels.get(parameter.name) ?? ""
					const displayValue = connectionInfo.resolvedInputs.has(parameter.name)
						? connectionInfo.values[parameter.name]
						: parameter.value
					const value =
						typeof displayValue === "object"
							? JSON.stringify(displayValue)
							: String(displayValue ?? "")
					return [
						parameter.name,
						parameter.type,
						connectionInfo.inputs.has(parameter.name) ? "1" : "0",
						sourceLabel,
						value,
					].join(":")
				})
				.join("|"),
		[
			parameters,
			connectionInfo.inputs,
			connectionInfo.resolvedInputs,
			connectionInfo.sourceLabels,
			connectionInfo.values,
		],
	)
	const nodeInternalsKey = [
		data.code,
		data.outputs.join("|"),
		inputHandleKey,
		parameterLayoutKey,
		builtInControlKind ?? "",
	].join("\n")

	useEffect(() => {
		const frame = requestAnimationFrame(() => {
			if (nodeInternalsKey) updateNodeInternals(data.id)
		})
		return () => cancelAnimationFrame(frame)
	}, [data.id, nodeInternalsKey, updateNodeInternals])

	return (
		<div
			className={cn(
				"group/node relative inline-flex overflow-visible transition-[box-shadow,transform]",
				!imageSource && "min-w-[220px]",
				imageSource && "min-w-0",
				railConfig && "min-w-[371px]",
			)}
			style={
				imageSource && imageDimensions
					? { width: imageDimensions.width }
					: undefined
			}
		>
			<div
				className={cn(
					"relative inline-flex w-full min-w-0 flex-col rounded-[20px]",
					isSelected ? "bg-[var(--node-selection)]" : "bg-[var(--node-bg)]",
				)}
			>
				{imageSource ? (
					<NodeSurface selected={isSelected} className="overflow-hidden">
						<ImageNode
							key={imageSource}
							source={imageSource}
							label={data.label}
							onDimensionsChange={handleImageDimensionsChange}
						/>
					</NodeSurface>
				) : data.type === "react" ? (
					<NodeSurface selected={isSelected} className="overflow-hidden">
						{parameters.length > 0 ||
						typeof data.metadata.sentence === "string" ||
						typeof data.metadata.description === "string" ? (
							<SentenceRenderer
								sentence={
									typeof data.metadata.sentence === "string"
										? data.metadata.sentence
										: typeof data.metadata.description === "string"
											? data.metadata.description
											: ""
								}
								parameters={parameters}
								connectionInfo={connectionInfo}
								onParameterChange={handleParameterChange}
								variant="canvas"
								isNodeSelected={isSelected}
								className={cn(
									"w-full px-[10px] pt-[5px]",
									readOnly && "pointer-events-none",
								)}
							/>
						) : null}
						<ReactNodeRenderer
							code={data.code}
							nodeID={data.id}
							props={reactNodeProps}
							className="overflow-hidden"
						/>
					</NodeSurface>
				) : builtInControlKind ? (
					<NodeSurface selected={isSelected} className="overflow-hidden">
						<div
							aria-disabled={readOnly}
							className={cn(readOnly && "pointer-events-none")}
						>
							<BuiltInControl
								kind={builtInControlKind}
								inputs={data.inputs}
								onParameterChange={handleParameterChange}
							/>
						</div>
					</NodeSurface>
				) : railConfig ? (
					<NodeSurface selected={isSelected} className="overflow-hidden">
						<NodeValueRail
							config={railConfig}
							selected={isSelected}
							onParameterChange={handleParameterChange}
							className={cn("w-full", readOnly && "pointer-events-none")}
						/>
					</NodeSurface>
				) : (
					<NodeSurface
						selected={isSelected}
						className="overflow-hidden px-[10px] py-[5px]"
					>
						<SentenceRenderer
							sentence={
								typeof data.metadata.sentence === "string"
									? data.metadata.sentence
									: typeof data.metadata.description === "string"
										? data.metadata.description
										: ""
							}
							parameters={parameters}
							connectionInfo={connectionInfo}
							onParameterChange={handleParameterChange}
							variant="canvas"
							isNodeSelected={isSelected}
							className={cn("w-full", readOnly && "pointer-events-none")}
						/>
					</NodeSurface>
				)}
				<NodeHeader
					label={data.label}
					onDelete={readOnly ? undefined : handleDeleteNode}
					variant={railConfig ? "rail" : "compute"}
					selected={isSelected}
					leading={
						data.outputs.length > 0 ? (
							<span className="flex shrink-0 items-center gap-1">
								{data.outputs.map((output) => (
									<span
										key={output}
										className="relative size-[22px] shrink-0"
										title={output}
									>
										<OutputHandle
											position={Position.Bottom}
											id={output}
											tone={outputTone}
											className="!bottom-auto !left-0 !top-0 !translate-x-0 !translate-y-0"
											style={{
												height: 22,
												minHeight: 22,
												minWidth: 22,
												width: 22,
											}}
										/>
									</span>
								))}
							</span>
						) : null
					}
					className="px-[10px] py-[8px]"
				/>
			</div>
		</div>
	)
}
